import { api } from "@/lib/apiClient";
import { DOKUMENT_TYP_LABELS } from "@/lib/dokumente";

/** Dokumenttypen mit konfigurierbarer GD-Startnummer (ohne Stundennachweis). */
export const GD_NUMMER_TYPEN = [
  "angebot",
  "rechnung",
  "lieferschein",
  "auftragsbestaetigung",
] as const;

export type GdNummerTyp = (typeof GD_NUMMER_TYPEN)[number];

export interface GdNummerTypEinstellung {
  startnummer: number;
  last_lauf: number;
  naechste_lauf: number;
  naechste_nummer: string;
}

export interface GdNummernEinstellungen {
  jahr: number;
  nummern: Record<GdNummerTyp, GdNummerTypEinstellung>;
}

export type GdStartnummernPayload = Record<GdNummerTyp, number>;

export function gdNummerTypLabel(typ: GdNummerTyp): string {
  return DOKUMENT_TYP_LABELS[typ] || typ;
}

export function emptyGdStartnummern(): GdStartnummernPayload {
  return {
    angebot: 1,
    rechnung: 1,
    lieferschein: 1,
    auftragsbestaetigung: 1,
  };
}

const GD_TYP_PREFIX: Record<GdNummerTyp, string> = {
  angebot: "AN",
  rechnung: "RE",
  lieferschein: "LS",
  auftragsbestaetigung: "AB",
};

export function buildGdNummerPreview(typ: GdNummerTyp, jahr: number, lauf: number): string {
  const prefix = GD_TYP_PREFIX[typ] || "GD";
  const yy = String(jahr).slice(-2);
  const padded = String(Math.max(1, Math.min(9999, lauf))).padStart(4, "0");
  return `${prefix}${yy}-${padded}`;
}

export function previewNaechsteNummer(
  typ: GdNummerTyp,
  jahr: number,
  startnummer: number,
  lastLauf: number,
): string {
  const naechste = Math.max((lastLauf || 0) + 1, startnummer || 1);
  return buildGdNummerPreview(typ, jahr, naechste);
}

export function startnummernFromEinstellungen(
  data: GdNummernEinstellungen | null | undefined,
): GdStartnummernPayload {
  const base = emptyGdStartnummern();
  if (!data?.nummern) return base;
  for (const typ of GD_NUMMER_TYPEN) {
    const item = data.nummern[typ];
    if (item?.startnummer != null) {
      base[typ] = Math.max(1, Math.min(9999, Number(item.startnummer) || 1));
    }
  }
  return base;
}

export async function getGdNummernEinstellungen(): Promise<GdNummernEinstellungen> {
  return api.get<GdNummernEinstellungen>("/dokumente/einstellungen/nummern");
}

export async function saveGdNummernEinstellungen(
  startnummern: GdStartnummernPayload,
): Promise<GdNummernEinstellungen> {
  return api.put<GdNummernEinstellungen>("/dokumente/einstellungen/nummern", startnummern);
}
